import { useCallback } from 'react'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import { AnswerID } from '@aytm/app/swidget/base/questions/types'
import { getMediumAssetPath } from '@aytm/lib/common/assets'
import { unescapeTags } from '@aytm/lib/common/func'
import { IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../../types.ts'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import { useChildrenProducts } from '../../hooks/useChildrenProducts.tsx'
import { hiddenClass } from '../../utils/hiddenClass.ts'
import AddToCartButton from './AddToCartButton.tsx'
import Rating from './Rating.tsx'
import style from './ProductCard.module.scss'

interface IProductCardProps {
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  amount: number
  findability: boolean
  disabled?: boolean
  registerCardRef?: (id: AnswerID, element: HTMLDivElement | null) => void
  'data-testid'?: string
  onSelect: () => void
  onAdd: (qnt: number) => void
}

const FULFILMENT_CHIPS = [
  { key: 'chipShipping', icon: 'pi-box' },
  { key: 'chipPickup', icon: 'pi-shopping-bag' },
  { key: 'chipDelivery', icon: 'pi-bolt' }
]

const ProductCard = ({
  schema,
  answer,
  amount,
  findability,
  disabled = false,
  registerCardRef,
  'data-testid': dataTestId,
  onSelect,
  onAdd
}: IProductCardProps) => {
  const { t, languageTag } = useSurveyLocale()
  const viewport = useViewport()
  const hasDiscount = Boolean(answer.discounted_price) && answer.price > answer.discounted_price
  // A rating below one star is unreachable for a real product, so it is treated as no rating.
  const hasRating = Number(answer.rating) >= 1
  const hasVariants = useChildrenProducts({ answer, schema, excludeCurrentProduct: true }).length > 0
  const price = (value: number) => formatPriceWithLocale(value, schema.price_mask, languageTag, true, false)
  const discountOff = () => {
    const diff = answer.price - answer.discounted_price
    return schema.price_mask.replace('{{price}}', diff.toFixed(2))
  }
  const setCardRef = useCallback(
    (element: HTMLDivElement | null) => registerCardRef?.(answer.id, element),
    [answer.id, registerCardRef]
  )

  const badges = (hasDiscount || answer.red_label || answer.green_label) && (
    <div className={`${style.badges} ${viewport.mobile ? style.badgesInline : ''}`}>
      {hasDiscount && (
        <span className={style.instantSavings}>{t('questions', 'ec', 'samsclub', 'instantSavings')}</span>
      )}
      {answer.red_label && <span className={style.redLabel} dangerouslySetInnerHTML={{ __html: answer.red_label }} />}
      {answer.green_label && (
        <span className={style.greenLabel} dangerouslySetInnerHTML={{ __html: answer.green_label }} />
      )}
    </div>
  )

  const favorite = (
    <span className={style.favoriteWrap} onClick={event => event.stopPropagation()}>
      <EmptyLink color="#0a6cff">
        <span
          className={`${style.favorite} ${viewport.mobile ? style.favoriteMobile : ''}`}
          role="img"
          aria-label={t('questions', 'ec', 'samsclub', 'saveForLater')}
        >
          <i className="pi pi-heart" />
        </span>
      </EmptyLink>
    </span>
  )

  const image = answer.image ? (
    <div
      className={style.image}
      role="img"
      aria-label={answer.image.alt_text || t('questions', 'img', 'noAlt')}
      style={{ backgroundImage: `url('${getMediumAssetPath(answer.image)}')` }}
    />
  ) : (
    <div className={style.noImage} />
  )

  const cta = (
    <AddToCartButton
      amount={amount}
      findability={findability}
      limit={schema.max_qty_per_product}
      disabled={disabled}
      spread={viewport.mobile}
      view={hasVariants}
      onView={onSelect}
      onAdd={onAdd}
    />
  )

  const priceRow = (
    <div className={style.priceRow}>
      {hasDiscount ? (
        <>
          <span className={style.now}>{t('questions', 'ec', 'samsclub', 'nowPrice')}</span>
          <span className={style.priceSale} dangerouslySetInnerHTML={{ __html: price(answer.discounted_price) }} />
          <span className={style.priceOld} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
        </>
      ) : (
        <span className={style.price} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
      )}
      {answer.per_count_price && (
        <span className={style.unitPrice} dangerouslySetInnerHTML={{ __html: unescapeTags(answer.per_count_price) }} />
      )}
    </div>
  )

  const saveOff = hasDiscount && (
    <div className={style.saveOff}>
      {t<(amount: string) => string>('questions', 'ec', 'samsclub', 'saveOff')(discountOff())}
    </div>
  )

  const title = <div className={style.title} dangerouslySetInnerHTML={{ __html: answer.text }} />

  const rating = hasRating && (
    <div className={style.ratingRow}>
      <Rating rating={answer.rating} />
      {answer.reviews != null && <span className={style.reviews}>{answer.reviews.toLocaleString(languageTag)}</span>}
    </div>
  )

  const fulfilment = (
    <div className={style.fulfilment}>
      {FULFILMENT_CHIPS.map(({ key, icon }) => (
        <EmptyLink key={key} color="#0a6cff">
          <span className={style.fulfilmentChip}>
            <i className={`pi ${icon} ${style.fulfilmentIcon}`} />
            {t('questions', 'ec', 'samsclub', key)}
          </span>
        </EmptyLink>
      ))}
    </div>
  )

  if (viewport.mobile) {
    return (
      <div
        ref={setCardRef}
        data-testid={dataTestId}
        aria-disabled={disabled}
        className={`${style.cardMobile} ${hiddenClass(answer)} ${disabled ? style.disabled : ''}`}
      >
        <div className={style.mobileImageWrapper} onClick={onSelect}>
          {favorite}
          {image}
        </div>

        <div className={style.mobileBody}>
          <div className={style.mobileInfo} onClick={onSelect}>
            {badges}
            {answer.sponsored && <div className={style.sponsored}>{t('questions', 'ec', 'samsclub', 'sponsored')}</div>}
            {priceRow}
            {saveOff}
            {title}
          </div>
          {rating}
          {fulfilment}
          <div className={style.mobileCta}>{cta}</div>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={setCardRef}
      data-testid={dataTestId}
      aria-disabled={disabled}
      className={`${style.card} ${hiddenClass(answer)} ${disabled ? style.disabled : ''}`}
    >
      <div className={style.imageWrapper} onClick={onSelect}>
        {badges}
        {favorite}
        {image}
      </div>

      <div className={style.cta}>{cta}</div>

      <div className={style.body} onClick={onSelect}>
        {answer.sponsored && <div className={style.sponsored}>{t('questions', 'ec', 'samsclub', 'sponsored')}</div>}
        {priceRow}
        {saveOff}
        {title}
      </div>

      {rating}
      {fulfilment}
    </div>
  )
}

export default ProductCard
